import { useMemo } from "react";

import {
  DentalBenefits,
  DentalCoverage,
  GeneralInformation,
  PlanRates,
} from "../../constants/aquafina";
import {
  DENTAL_VARIANT_PLAN_TYPE_MAPPING,
  PolicyVariants,
  VIEW_MODES,
} from "../../constants";
import CollapsableSection from "../sections/collapsable-section";
import { OutOfNetworkDefaultValue, RangeIterator } from "../../utils";
import ContributionType from "../sections/contribution-type-section";
import {
  CoverageDividerRow,
  CoverageRow,
  CoverageHeaderRow,
  NotProvidedRow,
} from "../table/rows";
import {
  CoverageVectorCell,
  CoverageVectorHeaderCell,
  CoverageCell,
} from "../table/cells";
import Rates from "../rates";
import { ViewMode } from "../../constants/types";
import PolicyGeneralInfo from "../sections/policy-general-info";
import ProposalGeneralInfo from "../sections/proposal-general-info";

const NOT_COVERED = "Not Covered";

type Props = {
  viewMode: ViewMode;
  coverage: DentalCoverage;
  variant: PolicyVariants;
  displayV4Fields: boolean;
};

function Dental({ coverage, variant, viewMode, displayV4Fields }: Props) {
  const state = useMemo(() => {
    const benefitDetails: DentalBenefits[] = [];
    const generalInfo: GeneralInformation[] = [];
    const rates: PlanRates[] = [];
    const fundingTypes: (string | null)[] = [];
    const planType =
      DENTAL_VARIANT_PLAN_TYPE_MAPPING[
        variant as keyof typeof DENTAL_VARIANT_PLAN_TYPE_MAPPING
      ];
    const plans = coverage.plans.filter((p) => p.planType === planType);
    let inconsistentRateData = false;

    RangeIterator(plans.length, (index) => {
      const plan = plans[index];
      benefitDetails.push(plan.benefitDetails);
      generalInfo.push(plan.generalInformation);
      fundingTypes.push(plan.generalInformation?.fundingType);
      rates.push(plan.planRates);
      if (plan.inconsistentRateData === true) {
        inconsistentRateData = true;
      }
    });
    return {
      benefitDetails,
      generalInfo,
      fundingTypes,
      plans,
      rates,
      inconsistentRateData,
      planType,
    };
  }, [coverage.plans, variant]);

  return (
    <>
      {viewMode === VIEW_MODES.POLICY ? (
        <>
          <PolicyGeneralInfo
            data={state.generalInfo}
            hideNetworkProviderName={!displayV4Fields}
          />
          <ContributionType plans={state.plans} />
        </>
      ) : (
        <ProposalGeneralInfo
          data={state.generalInfo}
          showFundingType
          hideNetworkProviderName={!displayV4Fields}
        />
      )}
      <DentalBenefitDetails
        benefits={state.benefitDetails}
        planType={state.planType}
        fundingTypes={state.fundingTypes}
        displayV4Fields={displayV4Fields}
      />
      <Rates
        rates={state.rates}
        totalPlans={
          state.rates.length === 0 ? state.plans.length : state.rates.length
        }
        inconsistentRateData={state.inconsistentRateData}
      />
    </>
  );
}

type DentalBenefitsDetailsProps = {
  benefits: DentalBenefits[];
  planType: string;
  fundingTypes: (string | null)[];
  displayV4Fields: boolean;
};

const DentalBenefitDetails = ({
  benefits,
  planType,
  fundingTypes,
  displayV4Fields,
}: DentalBenefitsDetailsProps) => (
  <CollapsableSection numberOfColumns={benefits.length} title="Benefit Details">
    <DentalBenefitDetailsData
      fundingTypes={fundingTypes}
      benefits={benefits}
      planType={planType}
      displayV4Fields={displayV4Fields}
    />
  </CollapsableSection>
);

const DentalBenefitDetailsData = ({
  benefits,
  planType,
  fundingTypes,
  displayV4Fields,
}: DentalBenefitsDetailsProps) => {
  if (benefits.every((b) => b === null)) {
    return (
      <NotProvidedRow
        colSpan={benefits.length}
        notProvidedText={planType === "hmo" ? "Fee Schedule Not Provided." : ""}
      />
    );
  }

  return (
    <>
      <CoverageHeaderRow isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorHeaderCell
            label={["In Network", "Out of Network"]}
            key={index}
          />
        ))}
      </CoverageHeaderRow>
      <CoverageDividerRow label="Deductible" totalPlans={benefits.length} />
      <CoverageRow label="Individual Deductible" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.individualDeductible,
              benefits[index].outOfNetwork?.individualDeductible ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Family Deductible">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.familyDeductible,
              benefits[index].outOfNetwork?.familyDeductible ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Deductible Waived for Preventive" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].isDeductibleWaivedPreventative}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Deductible Duration">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].deductibleDuration}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageDividerRow
        label="Maximums"
        totalPlans={benefits.length}
        isWhite
      />
      <CoverageRow label="Annual Maximum">
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.annualMaximum,
              benefits[index].outOfNetwork?.annualMaximum ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Ortho Maximum" isWhite>
        {RangeIterator(benefits.length, (index) =>
          benefits[index].inNetwork?.orthoMaximum === NOT_COVERED ? (
            <CoverageCell value={NOT_COVERED} key={index} />
          ) : (
            <CoverageVectorCell
              value={[
                benefits[index].inNetwork?.orthoMaximum,
                benefits[index].outOfNetwork?.orthoMaximum ||
                  OutOfNetworkDefaultValue(
                    benefits[index].outOfNetwork,
                    fundingTypes[index],
                  ),
              ]}
              key={index}
            />
          ),
        )}
      </CoverageRow>

      <CoverageRow label="Ortho Age Limit">
        {RangeIterator(benefits.length, (index) =>
          benefits[index].inNetwork?.orthoAgeLimit === NOT_COVERED ? (
            <CoverageCell value={NOT_COVERED} key={index} />
          ) : (
            <CoverageVectorCell
              value={[
                benefits[index].inNetwork?.orthoAgeLimit,
                benefits[index].outOfNetwork?.orthoAgeLimit ||
                  OutOfNetworkDefaultValue(
                    benefits[index].outOfNetwork,
                    fundingTypes[index],
                  ),
              ]}
              key={index}
            />
          ),
        )}
      </CoverageRow>
      <CoverageRow label="Out of Network Reimbursement" isWhite>
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell
            value={benefits[index].outOfNetworkReimbursement}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Rollover Maximum">
        {RangeIterator(benefits.length, (index) => (
          <CoverageCell value={benefits[index].maximumRollover} key={index} />
        ))}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Missing Tooth Exclusion" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].missingToothExclusion}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Annual Open Enrollment">
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].annualOpenEnrollment}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Dependent Age Limit" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageCell
                value={benefits[index].dependentAgeLimit}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageDividerRow
        label="Coinsurance & Services"
        totalPlans={benefits.length}
        isWhite={!displayV4Fields}
      />
      <CoverageRow label="Preventive - Type 1" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.preventativeType1,
              benefits[index].outOfNetwork?.preventativeType1 ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Basic - Type 2" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.basicType2,
              benefits[index].outOfNetwork?.basicType2 ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Major - Type 3" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.majorType3,
              benefits[index].outOfNetwork?.majorType3 ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Ortho - Type 4" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) =>
          benefits[index].inNetwork?.orthoType4 === NOT_COVERED ? (
            <CoverageCell value={NOT_COVERED} key={index} />
          ) : (
            <CoverageVectorCell
              value={[
                benefits[index].inNetwork?.orthoType4,
                benefits[index].outOfNetwork?.orthoType4 ||
                  OutOfNetworkDefaultValue(
                    benefits[index].outOfNetwork,
                    fundingTypes[index],
                  ),
              ]}
              key={index}
            />
          ),
        )}
      </CoverageRow>
      {displayV4Fields ? (
        <>
          <CoverageRow label="Sealants" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.sealants,
                  benefits[index].outOfNetwork?.sealants ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Space Maintainers">
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.spaceMaintainers,
                  benefits[index].outOfNetwork?.spaceMaintainers ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="General Anesthesia" isWhite>
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.generalAnesthesia,
                  benefits[index].outOfNetwork?.generalAnesthesia ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
          <CoverageRow label="Composite (White) Fillings">
            {RangeIterator(benefits.length, (index) => (
              <CoverageVectorCell
                value={[
                  benefits[index].inNetwork?.compositeWhiteFillings,
                  benefits[index].outOfNetwork?.compositeWhiteFillings ||
                    OutOfNetworkDefaultValue(
                      benefits[index].outOfNetwork,
                      fundingTypes[index],
                    ),
                ]}
                key={index}
              />
            ))}
          </CoverageRow>
        </>
      ) : null}
      <CoverageRow label="Endodontics" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.endodontics,
              benefits[index].outOfNetwork?.endodontics ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow
        label="Periodontics (Non-Surgical)"
        isWhite={!displayV4Fields}
      >
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.periodonticsNonSurgical,
              benefits[index].outOfNetwork?.periodonticsNonSurgical ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Periodontics (Surgical)" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.periodonticsSurgical,
              benefits[index].outOfNetwork?.periodonticsSurgical ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Oral Surgery (Simple)" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.oralSurgerySimple,
              benefits[index].outOfNetwork?.oralSurgerySimple ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Oral Surgery (Complex)" isWhite={displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.oralSurgeryComplex,
              benefits[index].outOfNetwork?.oralSurgeryComplex ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
      <CoverageRow label="Implants" isWhite={!displayV4Fields}>
        {RangeIterator(benefits.length, (index) => (
          <CoverageVectorCell
            value={[
              benefits[index].inNetwork?.implants,
              benefits[index].outOfNetwork?.implants ||
                OutOfNetworkDefaultValue(
                  benefits[index].outOfNetwork,
                  fundingTypes[index],
                ),
            ]}
            key={index}
          />
        ))}
      </CoverageRow>
    </>
  );
};

export default Dental;
